/**
 * Navigation icons, drawn as pixel bitmaps.
 *
 * The design system says no asset packs and no icon fonts, and the previous
 * icons were Unicode glyphs — which meant the rail rendered differently on
 * every OS and two of them (🌱 and the like) arrived as full-colour emoji that
 * fought the palette. A glyph is also whatever the font vendor decided it is;
 * you cannot nudge it.
 *
 * So each icon is a 10x10 grid written as ASCII art: `#` is a filled pixel and
 * `.` is empty. That keeps them legible and editable in source, guarantees they
 * match the pixel language of the rest of the chrome, and — because every pixel
 * is `currentColor` — lets each nav group tint its own icons simply by setting
 * a colour on the link.
 *
 * Adding one is a new entry in ICONS. Nothing else needs to change.
 */

export type IconName = keyof typeof ICONS;

const ICONS = {
  home: [
    '....##....',
    '...####...',
    '..######..',
    '.########.',
    '##########',
    '.#......#.',
    '.#..##..#.',
    '.#..##..#.',
    '.#..##..#.',
    '.########.',
  ],
  // A sapling: canopy over a trunk, the same shape the Grove backdrops use.
  grove: [
    '...####...',
    '..######..',
    '.########.',
    '##########',
    '.########.',
    '..######..',
    '....##....',
    '....##....',
    '...####...',
    '..######..',
  ],
  world: [
    '...####...',
    '..#....#..',
    '.#.####.#.',
    '#..#..#..#',
    '##########',
    '#..####..#',
    '.#.#..#.#.',
    '..#....#..',
    '...####...',
    '..........',
  ],
  // An egg: narrow at the top, heavy at the base. The earlier version was
  // symmetrical and read as a gem rather than an egg.
  egg: [
    '...####...',
    '..######..',
    '.########.',
    '.########.',
    '##########',
    '##########',
    '##########',
    '##########',
    '.########.',
    '..######..',
  ],
  // A tulip for the Nursery — where eggs are bred rather than hatched.
  // Three distinct lobes, because a rounder bloom was indistinguishable from
  // the Friends heart at 15px.
  flower: [
    '.#..##..#.',
    '.#.####.#.',
    '.##.##.##.',
    '.########.',
    '.########.',
    '..######..',
    '....##....',
    '....##....',
    '..###.....',
    '....##....',
  ],
  // Stacked eggs in a basket: the market, as opposed to a single egg.
  basket: [
    '..##..##..',
    '.####.####',
    '.####.####',
    '..##..##..',
    '##########',
    '#........#',
    '#.######.#',
    '#........#',
    '.#......#.',
    '..######..',
  ],
  // A Poke Ball, for the collection.
  ball: [
    '..######..',
    '.########.',
    '##########',
    '###....###',
    '##.####.##',
    '##.####.##',
    '###....###',
    '##########',
    '.########.',
    '..######..',
  ],
  book: [
    '.########.',
    '.#......#.',
    '.#.####.#.',
    '.#......#.',
    '.#.####.#.',
    '.#......#.',
    '.#.####.#.',
    '.#......#.',
    '.########.',
    '..........',
  ],
  // A ruled list — the Pokedex as an index rather than a device.
  list: [
    '##########',
    '#........#',
    '#.##.###.#',
    '#........#',
    '#.##.###.#',
    '#........#',
    '#.##.###.#',
    '#........#',
    '##########',
    '..........',
  ],
  star: [
    '....##....',
    '....##....',
    '...####...',
    '##########',
    '.########.',
    '..######..',
    '..######..',
    '.##....##.',
    '.#......#.',
    '..........',
  ],
  // Two figures side by side: the community.
  people: [
    '..##..##..',
    '..##..##..',
    '..........',
    '.####.####',
    '########.#',
    '########.#',
    '.########.',
    '.##....##.',
    '.##....##.',
    '..........',
  ],
  // A pennant on a pole. The previous framed rectangle read as a monitor.
  banner: [
    '##........',
    '##.#####..',
    '##.######.',
    '##.#######',
    '##.######.',
    '##.#####..',
    '##........',
    '##........',
    '##........',
    '##........',
  ],
  heart: [
    '.##....##.',
    '####..####',
    '##########',
    '##########',
    '##########',
    '.########.',
    '..######..',
    '...####...',
    '....##....',
    '..........',
  ],
  // Two arrows passing: a trade.
  swap: [
    '...#......',
    '..##......',
    '.#########',
    '..##......',
    '...#......',
    '......#...',
    '......##..',
    '#########.',
    '......##..',
    '......#...',
  ],
  // A lightning bolt, for the World Boss. Deliberately not a sword or a skull:
  // the fight is a storm the whole town turns out for, and the first Legendary
  // this system is built around is Zapdos.
  bolt: [
    '.....###..',
    '....###...',
    '...###....',
    '..#####...',
    '.######...',
    '...#####..',
    '....###...',
    '...###....',
    '..###.....',
    '.###......',
  ],
  // A four-point sparkle for Events.
  sparkle: [
    '....##....',
    '....##....',
    '..#.##.#..',
    '...####...',
    '##########',
    '##########',
    '...####...',
    '..#.##.#..',
    '....##....',
    '....##....',
  ],
  // A podium: first, second, third.
  podium: [
    '...####...',
    '...#..#...',
    '...####...',
    '...####...',
    '##.####...',
    '##.####.##',
    '##.####.##',
    '##.####.##',
    '##.####.##',
    '##########',
  ],
  // A market stall with an awning.
  stall: [
    '##########',
    '#.##.##.#.',
    '#.##.##.#.',
    '##########',
    '#........#',
    '#..####..#',
    '#..#..#..#',
    '#..#..#..#',
    '#..#..#..#',
    '##########',
  ],
  // A shopping bag with handles.
  bag: [
    '..#....#..',
    '.###..###.',
    '.##....##.',
    '##########',
    '##########',
    '##########',
    '##########',
    '##########',
    '##########',
    '.########.',
  ],
  person: [
    '...####...',
    '..######..',
    '..######..',
    '...####...',
    '..........',
    '.########.',
    '##########',
    '##########',
    '##########',
    '##########',
  ],
  gear: [
    '..#.##.#..',
    '..########',
    '.###..###.',
    '###....###',
    '##......##',
    '##......##',
    '###....###',
    '.###..###.',
    '..########',
    '..#.##.#..',
  ],
  // A shield, for admin-only routes.
  shield: [
    '.########.',
    '##########',
    '##########',
    '##########',
    '##########',
    '.########.',
    '.########.',
    '..######..',
    '...####...',
    '....##....',
  ],
  // A door with an arrow leaving it.
  exit: [
    '#######...',
    '#.....#...',
    '#.....#...',
    '#.....#.#.',
    '#.....##..',
    '#...#####.',
    '#.....##..',
    '#.....#.#.',
    '#.....#...',
    '#######...',
  ],
  // The Discord mark: a wide face, two eyes, and the tails flaring out at the
  // bottom. Drawn like every other icon rather than dropped in as the brand SVG
  // — the rail is one pixel language, and a vector logo at 15px next to these
  // would be the only smooth thing on the screen.
  discord: [
    '..######..',
    '.########.',
    '##########',
    '##..##..##',
    '##..##..##',
    '##########',
    '##########',
    '.########.',
    '.##....##.',
    '##......##',
  ],
  more: [
    '..........',
    '..........',
    '..........',
    '##.##.##..',
    '##.##.##..',
    '..........',
    '##.##.##..',
    '##.##.##..',
    '..........',
    '..........',
  ],
} satisfies Record<string, string[]>;

/**
 * Renders one bitmap as an SVG.
 *
 * Consecutive filled pixels in a row are merged into a single `<rect>`, which
 * takes a 10x10 grid from up to 100 elements down to a handful. `crispEdges`
 * stops the browser antialiasing the pixel boundaries at fractional sizes.
 */
export function Icon({ name, size = 15 }: { name: IconName; size?: number }) {
  const rows = ICONS[name];
  const rects: Array<{ x: number; y: number; w: number }> = [];

  rows.forEach((row, y) => {
    let x = 0;
    while (x < row.length) {
      if (row[x] !== '#') {
        x++;
        continue;
      }
      let width = 0;
      while (row[x + width] === '#') width++;
      rects.push({ x, y, w: width });
      x += width;
    }
  });

  return (
    <svg
      viewBox="0 0 10 10"
      width={size}
      height={size}
      fill="currentColor"
      shapeRendering="crispEdges"
      aria-hidden="true"
      focusable="false"
    >
      {rects.map((rect) => (
        <rect key={`${rect.x}-${rect.y}`} x={rect.x} y={rect.y} width={rect.w} height={1} />
      ))}
    </svg>
  );
}
